'use strict';

import { PreprocessorType, GroupType } from '../../model/enumerations';

GroupingPreprocessorFactory.$inject = [
	'mx.shell.SearchProviderService',
	'mx.SolutionBuilderAgent.BasePreprocessor',
	'mx.sb.Agent.DataQuery.FilterOperatorsFactory',
	'mx.SolutionBuilderAgent.SearchQueryGroup',
	'mx.SolutionBuilderAgent.SearchQueryCondition',
	'mx.SolutionBuilderAgent.PersonalizationService',
];
export default function GroupingPreprocessorFactory(searchService, BasePreprocessor, filterOperatorsFactory, SearchQueryGroup, SearchQueryCondition, personalizationService) {
	class GroupingPreprocessor extends BasePreprocessor {
		constructor() {
			super();
			this.type = PreprocessorType.Grouping;
			this.groupedColumnsSorting = {};
			this._groupPath = [];
			return this;
		}

		build() {
			this._addGroupingIntoSearch();
		}

		init(executor, parameters, dataQueryList) {
			super.init(executor, parameters, dataQueryList);
			this.groupingGroup = this._getGroupingGroup();
			let columns = [];
			let groupingTree = [];

			Object.defineProperties(this, {
				groupedColumns: {
					get: function () {
						return columns;
					},
					set: function (newColumns) {
						if (Array.isArray(newColumns)) {
							columns = newColumns;
						}
					},
				},
				groupingTree: {
					get: function () {
						return groupingTree;
					},
					set: function (newGroupingTree) {
						if (Array.isArray(newGroupingTree)) {
							groupingTree = newGroupingTree;
						}
					},
				},
				columns: {
					get: () => dataQueryList.columns,
				},
			});
		}

		process() {
			if (!this._internalProcess) {
				return this._reloadGroupingTree();
			}
			this._internalProcess = false;
		}

		isActive() {
			return this.groupedColumns.length > 0;
		}

		async load(widgetId) {
			if (!this.executor.supportsGrouping()) {
				this.columns.forEach((column) => (column.GroupingIsAvailable = false));
				return;
			}

			this._personalizationId = widgetId;
			this.loading = true;
			this._internalProcess = true;

			let groupedColumns = await personalizationService.getGroupingColumns(this._personalizationId);
			if (Array.isArray(groupedColumns)) {
				groupedColumns.forEach((groupedColumn) => {
					const column = this._getColumnByName(groupedColumn.Name);
					groupedColumn.Title = column?.Title;
				});
				groupedColumns = groupedColumns.filter((column) => column.Title);
			} else {
				groupedColumns = [];
			}

			groupedColumns.forEach((columns) => {
				this.groupedColumnsSorting[columns.Name] = columns.SortDirection === 'desc';
			});

			this._groupPath = await personalizationService.getGroupingFilter(this._personalizationId);
			if (!Array.isArray(this._groupPath)) {
				this._groupPath = [];
			}

			if (typeof this._groupPath[0] === 'object') {
				// to support previous personalization format
				this._groupPath = this._groupPath.map((group) => group?.Value);
			}

			await this._applyGroupedColumns(groupedColumns);
		}

		async enableGroupingForColumn(columnName) {
			const column = this._getColumnByName(columnName);
			if (!column.GroupingIsAvailable) {
				return;
			}

			const groupedColumns = this.groupedColumns.slice();

			groupedColumns.push({
				Name: column.Name,
				Title: column.Title,
				SortDirection: 'asc',
			});

			await this._applyGroupedColumns(groupedColumns);
		}

		async disableGroupingForColumn(columnName) {
			let groupedColumn = this.groupedColumns.find((groupedColumn) => groupedColumn.Name === columnName);
			if (!groupedColumn) {
				return;
			}
			const groupedColumns = this.groupedColumns.slice();
			groupedColumns.splice(groupedColumns.indexOf(groupedColumn), 1);

			await this._applyGroupedColumns(groupedColumns);
		}

		async toggleColumnGrouping(columnName) {
			const column = this._getColumnByName(columnName);

			if (column.GroupingIsActive) {
				await this.disableGroupingForColumn(column.Name);
			} else {
				await this.enableGroupingForColumn(column.Name);
			}
		}

		toggleGroupingSorting(groupingColumn) {
			groupingColumn.SortDirection = groupingColumn.SortDirection === 'desc' ? 'asc' : 'desc';
			this.groupedColumnsSorting[groupingColumn.Name] = groupingColumn.SortDirection === 'desc';

			personalizationService.savePersonalizedGrouping(this.groupedColumns, this._personalizationId);
		}

		async resetGrouping() {
			await this._applyGroupedColumns([]);
		}

		setActiveGroup(activeGroup) {
			this._updateSubTreeRecursively(this.groupingTree, (group) => {
				group.isActive = false;
				group.isEnabled = false;
			});

			const previousPath = this._groupPath;

			this._groupPath = [];
			this.activeGroup = activeGroup;

			if (activeGroup) {
				activeGroup.isActive = true;

				while (activeGroup) {
					activeGroup.isEnabled = true;
					this._groupPath.unshift(activeGroup.Value);
					activeGroup = activeGroup.Parent;
				}
			}

			this._prepareGroupingSearchConditions();

			if (!angular.equals(previousPath, this._groupPath)) {
				this._internalProcess = true;
				personalizationService.savePersonalizedGroupingFilter(this._groupPath, this._personalizationId);
				this.callChanged();
			}
		}

		async _reloadGroupingTree() {
			await this._applyGroupedColumns(this.groupedColumns);
		}

		_updateSubTreeRecursively(groups, updater) {
			groups.forEach((group) => {
				updater(group);
				if (Array.isArray(group.SubGroups)) {
					this._updateSubTreeRecursively(group.SubGroups, updater);
				}
			});
		}

		_getGroupByPath(groups, groupPath) {
			let found;
			groups.some((group) => {
				if (group.Value === groupPath[0]) {
					if (groupPath.length === 1) {
						found = group;
					} else if (Array.isArray(group.SubGroups) && groupPath.length > 1) {
						groupPath = groupPath.slice();
						groupPath.shift();
						found = this._getGroupByPath(group.SubGroups, groupPath) || group;
					}

					return true;
				}
			});

			return found;
		}

		async _applyGroupedColumns(groupedColumns = []) {
			if (!this.executor.supportsGrouping() || !Array.isArray(groupedColumns)) {
				return;
			}
			this.loading = true;
			let activeGroup;

			if (!angular.equals(this.groupedColumns, groupedColumns)) {
				this.groupedColumns = groupedColumns;
				personalizationService.savePersonalizedGrouping(this.groupedColumns, this._personalizationId);
			}

			const groupedColumnNames = this.groupedColumns.map((column) => column.Name);

			this.columns.forEach((column) => (column.GroupingIsActive = groupedColumnNames.includes(column.Name)));

			this.groupingTree = await this._getGroupingTree();

			if (this.groupingTree.length) {
				activeGroup = this._getGroupByPath(this.groupingTree, this._groupPath);

				if (!activeGroup && this.groupingTree.length) {
					const firstGroup = this.groupingTree.find((gr) => gr.Value) || this.groupingTree[0];
					const groupPath = this._getFirstDeepestGroupPath(firstGroup);
					activeGroup = this._getGroupByPath(this.groupingTree, groupPath);
				}
			}

			this.setActiveGroup(activeGroup);

			this.loading = false;
		}

		async _getGroupingTree() {
			if (!Array.isArray(this.groupedColumns) || !this.groupedColumns.length) {
				return [];
			}

			// executor.getGroupingTree uses the same requester as for data loading, so it is necessary to remove grouping conditions to request grouping tree
			this._removeGroupingFromSearch();
			const groupingTree = await this.executor.getGroupingTree(this.groupedColumns);
			this._addGroupingIntoSearch();

			return groupingTree;
		}

		_getFirstDeepestGroupPath(group) {
			let path = [group.Value];
			if (Array.isArray(group.SubGroups) && group.SubGroups.length) {
				const firstGroup = group.SubGroups.find((gr) => gr.Value) || group.SubGroups[0];
				path = path.concat(this._getFirstDeepestGroupPath(firstGroup));
			}
			return path;
		}

		_prepareGroupingSearchConditions() {
			this.groupingGroup.conditions = [];

			let group = this.activeGroup;

			while (group) {
				const condition = this._createCondition(group.Column, group.Value);
				this.groupingGroup.addCondition(condition);
				group = group.Parent;
			}
		}

		_addGroupingIntoSearch() {
			searchService.currentSearch().searchForm.setGroup(this.groupingGroup, GroupType.Grouping);
		}

		_removeGroupingFromSearch() {
			searchService.currentSearch().searchForm.removeGroup(this.groupingGroup);
		}

		_getGroupingGroup() {
			return new SearchQueryGroup({
				LogicalOperator: mx.SolutionBuilderAgent.SearchQueryLogicalOperator.AND,
			});
		}

		_getColumnByName(columnName) {
			return this.columns.find((column) => column.Name === columnName);
		}

		_createCondition(columnName, value) {
			const column = this._getColumnByName(columnName);
			const condition = new SearchQueryCondition({
				Property: column.Name,
				Type: column.AttributeSearchValueType,
				Operator: filterOperatorsFactory.operators.In.value,
				Value: [value],
			});

			condition.__property = column;
			return condition;
		}
	}

	return GroupingPreprocessor;
}
